// ═══════════════════════════════════════════════════════════════════════
// Part 7: Anomaly Detection Engine
// ═══════════════════════════════════════════════════════════════════════
// Modular, pure-function anomaly detectors.
// Each detector returns AnomalyFlag[] for a single row.
// The main `runAnomalyDetection` orchestrates all detectors.

import type {
  ExportJobRow,
  AnomalyConfig,
  AnomalyFlag,
  AnomalyType,
  RowAnomalyResult,
  AnomalySummary,
  ReturnTripWindow,
} from './types';
import { deriveInvoicePassengerName } from './transforms';

// ─── Helpers ────────────────────────────────────────────────────────

let flagCounter = 0;
function makeFlag(
  type: AnomalyType,
  severity: 'warning' | 'error',
  message: string,
  opts?: { detail?: string; numericDetail?: number; relatedRowId?: string }
): AnomalyFlag {
  flagCounter++;
  return {
    id: `af_${flagCounter}_${Date.now()}`,
    type,
    severity,
    message,
    ...opts,
  };
}

/** Normalize an address for comparison: lowercase, trim, remove punctuation */
function normalizeAddr(addr: string | null): string {
  if (!addr) return '';
  return addr.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

/** Parse a date+time into a timestamp for comparison */
function parseDateTime(date: string | null, time: string | null): number | null {
  if (!date) return null;
  const d = new Date(date + 'T' + (time || '00:00') + ':00');
  return isNaN(d.getTime()) ? null : d.getTime();
}

/** Check if two times are within a window */
function isWithinWindow(
  ts1: number | null,
  ts2: number | null,
  window: ReturnTripWindow
): boolean {
  if (ts1 === null || ts2 === null) return false;
  const diff = Math.abs(ts1 - ts2);
  if (window === 'same_day') {
    // Same calendar day
    const d1 = new Date(ts1).toISOString().slice(0, 10);
    const d2 = new Date(ts2).toISOString().slice(0, 10);
    return d1 === d2;
  }
  const hours = window === '24h' ? 24 : 48;
  return diff <= hours * 60 * 60 * 1000;
}

// ─── Pre-computed Indexes ───────────────────────────────────────────

interface RowIndex {
  /** Map passenger name → rows */
  byPassenger: Map<string, ExportJobRow[]>;
  /** Map account_id → rows */
  byAccount: Map<string, ExportJobRow[]>;
  /** Map normalized pickup → rows */
  byPickup: Map<string, ExportJobRow[]>;
  /** Map normalized dropoff → rows */
  byDropoff: Map<string, ExportJobRow[]>;
  /** Map normalized address (any) → rows that have it as PU or DO */
  byAnyAddress: Map<string, ExportJobRow[]>;
  /** All fares for computing stats */
  allFares: number[];
  /** Mean fare */
  meanFare: number;
  /** Std dev of fares */
  stdDevFare: number;
}

function buildIndex(rows: ExportJobRow[]): RowIndex {
  const byPassenger = new Map<string, ExportJobRow[]>();
  const byAccount = new Map<string, ExportJobRow[]>();
  const byPickup = new Map<string, ExportJobRow[]>();
  const byDropoff = new Map<string, ExportJobRow[]>();
  const byAnyAddress = new Map<string, ExportJobRow[]>();
  const allFares: number[] = [];

  for (const row of rows) {
    const pName = deriveInvoicePassengerName(row).toLowerCase().trim();
    if (pName) {
      if (!byPassenger.has(pName)) byPassenger.set(pName, []);
      byPassenger.get(pName)!.push(row);
    }

    const accId = (row.account_id || '').trim();
    if (accId) {
      if (!byAccount.has(accId)) byAccount.set(accId, []);
      byAccount.get(accId)!.push(row);
    }

    const pu = normalizeAddr(row.pick_up_location);
    if (pu) {
      if (!byPickup.has(pu)) byPickup.set(pu, []);
      byPickup.get(pu)!.push(row);

      if (!byAnyAddress.has(pu)) byAnyAddress.set(pu, []);
      byAnyAddress.get(pu)!.push(row);
    }

    const doff = normalizeAddr(row.drop_off_location);
    if (doff) {
      if (!byDropoff.has(doff)) byDropoff.set(doff, []);
      byDropoff.get(doff)!.push(row);

      if (!byAnyAddress.has(doff)) byAnyAddress.set(doff, []);
      byAnyAddress.get(doff)!.push(row);
    }

    const fare = Number(row.grand_total || 0);
    if (fare > 0) allFares.push(fare);
  }

  // Compute mean and stddev
  const n = allFares.length;
  const meanFare = n > 0 ? allFares.reduce((a, b) => a + b, 0) / n : 0;
  const variance = n > 1
    ? allFares.reduce((sum, f) => sum + (f - meanFare) ** 2, 0) / (n - 1)
    : 0;
  const stdDevFare = Math.sqrt(variance);

  return { byPassenger, byAccount, byPickup, byDropoff, byAnyAddress, allFares, meanFare, stdDevFare };
}

// ─── Detector: Fare Variance ────────────────────────────────────────

/**
 * Flag when likely outbound and return trips exist but fare difference > threshold.
 * Matches by: same passenger, swapped pickup/dropoff, within time window.
 *
 * UPGRADED v2: Now also detects:
 * 1. Classic return trips (swapped PU/DO) with price difference
 * 2. Common-address return trips (shares at least one address) with price difference
 * 3. Exact same route (same PU + same DO, same direction) with different prices —
 *    flagged as a price discrepancy that can be corrected
 */
export function detectFareVariance(
  row: ExportJobRow,
  config: AnomalyConfig,
  index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];
  const fare = Number(row.grand_total || 0);
  if (fare <= 0) return flags;

  const pName = deriveInvoicePassengerName(row).toLowerCase().trim();
  if (!pName) return flags;

  const candidates = index.byPassenger.get(pName) || [];
  const rowPU = normalizeAddr(row.pick_up_location);
  const rowDO = normalizeAddr(row.drop_off_location);
  const rowTs = parseDateTime(row.pu_date, row.pu_time);

  if (!rowPU || !rowDO) return flags;

  let flaggedReturn = false;
  let flaggedSameRoute = false;

  for (const other of candidates) {
    if (other.tc_id === row.tc_id) continue;

    const otherPU = normalizeAddr(other.pick_up_location);
    const otherDO = normalizeAddr(other.drop_off_location);
    const otherTs = parseDateTime(other.pu_date, other.pu_time);

    if (!otherPU && !otherDO) continue;
    if (!isWithinWindow(rowTs, otherTs, config.returnTripWindow)) continue;

    const otherFare = Number(other.grand_total || 0);
    if (otherFare <= 0) continue;

    const diff = Math.abs(fare - otherFare);

    // Check 1: Exact same route (same direction) — flag price difference
    const isSameRoute = (otherPU === rowPU && otherDO === rowDO);
    if (isSameRoute && !flaggedSameRoute) {
      if (diff > 0.01) {
        flags.push(makeFlag('fare_variance', diff > config.fareVarianceThreshold ? 'error' : 'warning',
          `Same route, different price: $${fare.toFixed(2)} vs $${otherFare.toFixed(2)} (diff $${diff.toFixed(2)})`,
          {
            detail: `Same route as Job ${other.job_id} — ${other.pick_up_location || '?'} → ${other.drop_off_location || '?'}. Price can be updated.`,
            numericDetail: diff,
            relatedRowId: other.tc_id,
          }
        ));
        flaggedSameRoute = true;
      }
      continue; // Don't also flag as return trip
    }

    // Check 2: Classic return trip (swapped PU/DO)
    const isReturn = (otherPU === rowDO && otherDO === rowPU);
    if (isReturn && !flaggedReturn && diff > config.fareVarianceThreshold) {
      flags.push(makeFlag('fare_variance', 'warning',
        `Return trip fare differs by $${diff.toFixed(2)} (this: $${fare.toFixed(2)}, return: $${otherFare.toFixed(2)})`,
        {
          detail: `Return trip: Job ${other.job_id} — ${other.pick_up_location || '?'} → ${other.drop_off_location || '?'}`,
          numericDetail: diff,
          relatedRowId: other.tc_id,
        }
      ));
      flaggedReturn = true;
      continue;
    }

    // Check 3: Common-address trip (shares at least one address but not exact same or reversed)
    if (!isReturn && !isSameRoute && !flaggedReturn) {
      const sharesAddress =
        (otherPU === rowDO) || (otherDO === rowPU) ||
        (otherPU === rowPU) || (otherDO === rowDO);

      if (sharesAddress && diff > config.fareVarianceThreshold) {
        flags.push(makeFlag('fare_variance', 'warning',
          `Related trip (shared address) fare differs by $${diff.toFixed(2)} (this: $${fare.toFixed(2)}, related: $${otherFare.toFixed(2)})`,
          {
            detail: `Related trip: Job ${other.job_id} — ${other.pick_up_location || '?'} → ${other.drop_off_location || '?'}`,
            numericDetail: diff,
            relatedRowId: other.tc_id,
          }
        ));
        flaggedReturn = true;
      }
    }

    // Stop if we've flagged both types
    if (flaggedReturn && flaggedSameRoute) break;
  }

  return flags;
}


// ─── Detector: Missing Return Trip ──────────────────────────────────

/**
 * UPGRADED: Flag when a trip appears to be outbound but no corresponding return trip found.
 * Only flags account-based trips (has account_id).
 *
 * New logic:
 * - Uses 48hr window by default (configurable)
 * - A "return" is found if within the time window, another trip by the same passenger
 *   has picked up OR dropped off at a COMMON address with this trip's pickup or dropoff.
 *   It does NOT have to be an exact reverse — the person may go to another address
 *   during the day and return home from a secondary address.
 * - Only the pickup and dropoff addresses need to share at least one common address.
 */
export function detectMissingReturn(
  row: ExportJobRow,
  config: AnomalyConfig,
  index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];

  // Only check account-based trips
  if (!row.account_id) return flags;
  const fare = Number(row.grand_total || 0);
  if (fare <= 0) return flags;

  const pName = deriveInvoicePassengerName(row).toLowerCase().trim();
  if (!pName) return flags;

  const candidates = index.byPassenger.get(pName) || [];
  const rowPU = normalizeAddr(row.pick_up_location);
  const rowDO = normalizeAddr(row.drop_off_location);
  const rowTs = parseDateTime(row.pu_date, row.pu_time);

  if (!rowPU || !rowDO || rowPU === rowDO) return flags;

  // Look for a return trip — upgraded logic:
  // A trip qualifies as a "return" if:
  // 1. It's by the same passenger
  // 2. Within the configured time window
  // 3. It shares at least one common address with this trip's PU or DO
  //    (i.e. the other trip's PU or DO matches this trip's PU or DO)
  let hasReturn = false;
  for (const other of candidates) {
    if (other.tc_id === row.tc_id) continue;

    const otherTs = parseDateTime(other.pu_date, other.pu_time);
    if (!isWithinWindow(rowTs, otherTs, config.returnTripWindow)) continue;

    const otherPU = normalizeAddr(other.pick_up_location);
    const otherDO = normalizeAddr(other.drop_off_location);

    if (!otherPU && !otherDO) continue;

    // Check if any address is shared:
    // - Other trip picked up at this trip's dropoff (classic return)
    // - Other trip dropped off at this trip's pickup (classic return)
    // - Other trip picked up at this trip's pickup (went back to same origin from different route)
    // - Other trip dropped off at this trip's dropoff (went to same destination from different route)
    const sharesAddress =
      (otherPU === rowDO) ||  // other picked up where this dropped off
      (otherDO === rowPU) ||  // other dropped off where this picked up
      (otherPU === rowPU) ||  // other picked up at same place (e.g. return via different route)
      (otherDO === rowDO);    // other dropped off at same place

    if (sharesAddress) {
      hasReturn = true;
      break;
    }
  }

  if (!hasReturn) {
    flags.push(makeFlag('missing_return', 'warning',
      `No return trip found within ${config.returnTripWindow === 'same_day' ? 'same day' : config.returnTripWindow}`,
      { detail: `${row.pick_up_location || '?'} → ${row.drop_off_location || '?'}` }
    ));
  }

  return flags;
}

// ─── Detector: Missing Required Fields ──────────────────────────────

/**
 * Flag when required fields are missing.
 * Respects CRM-derived flags (missing_project, missing_cost_code, missing_reference).
 */
export function detectMissingFields(
  row: ExportJobRow,
  _config: AnomalyConfig,
  _index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];
  const missing: string[] = [];

  if (!row.account_id || row.account_id.trim() === '') missing.push('Account ID');
  if (row.missing_crm_account) missing.push('CRM Account');
  if (row.missing_project) missing.push('Project (required)');
  if (row.missing_cost_code) missing.push('Cost Code (required)');
  if (row.missing_reference) missing.push('Reference (required)');
  if (row.missing_payment_row) missing.push('Payment Record');

  if (missing.length > 0) {
    flags.push(makeFlag('missing_fields', 'error',
      `Missing: ${missing.join(', ')}`,
      { detail: missing.join(', ') }
    ));
  }

  return flags;
}

// ─── Detector: Validation Issues ────────────────────────────────────

/**
 * Flag when payment validation flags are failing.
 */
export function detectValidationIssues(
  row: ExportJobRow,
  _config: AnomalyConfig,
  _index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];
  const issues: string[] = [];

  if (Number(row.eftpos_amt || 0) > 0 && !row.eftpos_valid) {
    issues.push('EFTPOS not validated');
  }
  if (Number(row.totalmobility_amt || 0) > 0 && !row.total_mobility_valid) {
    issues.push('Total Mobility not validated');
  }
  if (Number(row.tm_hoist_amt || 0) > 0 && !row.tm_hoist_valid) {
    issues.push('TM Hoist not validated');
  }
  if (row.dcp_id && !row.reconciled) {
    issues.push('Not reconciled');
  }

  if (issues.length > 0) {
    flags.push(makeFlag('validation_issue', 'error',
      issues.join('; '),
      { detail: issues.join(', ') }
    ));
  }

  return flags;
}

// ─── Detector: TM Mismatch ──────────────────────────────────────────

/**
 * Flag when Total Mobility amount exists but net_after_tm doesn't match expected.
 */
export function detectTMMismatch(
  row: ExportJobRow,
  _config: AnomalyConfig,
  _index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];

  const tmAmt = Number(row.totalmobility_amt || 0);
  const hoistAmt = Number(row.tm_hoist_amt || 0);
  const grandTotal = Number(row.grand_total || 0);
  const netAfterTm = Number(row.net_after_tm || 0);

  if (tmAmt <= 0 && hoistAmt <= 0) return flags;

  // Expected net = grand_total - tm - hoist
  const expectedNet = grandTotal - tmAmt - hoistAmt;
  const diff = Math.abs(netAfterTm - expectedNet);

  if (diff > 0.01 && grandTotal > 0) {
    flags.push(makeFlag('tm_mismatch', 'warning',
      `TM deduction mismatch: expected net $${expectedNet.toFixed(2)}, got $${netAfterTm.toFixed(2)} (diff $${diff.toFixed(2)})`,
      { numericDetail: diff }
    ));
  }

  // Also flag if TM amount > grand total (shouldn't happen)
  if (tmAmt > grandTotal && grandTotal > 0) {
    flags.push(makeFlag('tm_mismatch', 'warning',
      `TM amount ($${tmAmt.toFixed(2)}) exceeds grand total ($${grandTotal.toFixed(2)})`,
      { numericDetail: tmAmt - grandTotal }
    ));
  }

  return flags;
}

// ─── Detector: Duplicate / Near-Duplicate Trips ─────────────────────

/**
 * Flag when a very similar trip exists: same passenger, similar pickup/dropoff, similar time.
 */
export function detectDuplicateTrips(
  row: ExportJobRow,
  _config: AnomalyConfig,
  index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];

  const pName = deriveInvoicePassengerName(row).toLowerCase().trim();
  if (!pName) return flags;

  const candidates = index.byPassenger.get(pName) || [];
  const rowPU = normalizeAddr(row.pick_up_location);
  const rowDO = normalizeAddr(row.drop_off_location);
  const rowTs = parseDateTime(row.pu_date, row.pu_time);

  if (!rowPU || !rowDO) return flags;

  for (const other of candidates) {
    if (other.tc_id === row.tc_id) continue;
    // Only flag if this row's tc_id is "first" alphabetically to avoid double-flagging
    if (row.tc_id > other.tc_id) continue;

    const otherPU = normalizeAddr(other.pick_up_location);
    const otherDO = normalizeAddr(other.drop_off_location);

    // Same direction (not return trip)
    if (otherPU !== rowPU || otherDO !== rowDO) continue;

    // Within 2 hours
    const otherTs = parseDateTime(other.pu_date, other.pu_time);
    if (rowTs !== null && otherTs !== null) {
      const timeDiff = Math.abs(rowTs - otherTs);
      if (timeDiff > 2 * 60 * 60 * 1000) continue; // More than 2 hours apart
    } else {
      // If we can't compare times, at least check same date
      if (row.pu_date !== other.pu_date) continue;
    }

    flags.push(makeFlag('duplicate_trip', 'warning',
      `Possible duplicate: same passenger, route, and similar time as Job ${other.job_id}`,
      { detail: `Job ${other.job_id}`, relatedRowId: other.tc_id }
    ));
    break; // Only flag once
  }

  return flags;
}

// ─── Detector: Outlier Pricing ──────────────────────────────────────

/**
 * Flag when fare is significantly outside the typical range for the dataset.
 * Uses mean ± N standard deviations.
 */
export function detectOutlierPricing(
  row: ExportJobRow,
  config: AnomalyConfig,
  index: RowIndex
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];

  const fare = Number(row.grand_total || 0);
  if (fare <= 0) return flags;
  if (index.allFares.length < 5) return flags; // Need enough data
  if (index.stdDevFare <= 0) return flags;

  const multiplier = config.outlierStdDevMultiplier;
  const lowerBound = index.meanFare - multiplier * index.stdDevFare;
  const upperBound = index.meanFare + multiplier * index.stdDevFare;

  if (fare > upperBound) {
    const deviation = ((fare - index.meanFare) / index.stdDevFare).toFixed(1);
    flags.push(makeFlag('outlier_pricing', 'warning',
      `Fare $${fare.toFixed(2)} is ${deviation} std devs above mean ($${index.meanFare.toFixed(2)})`,
      { numericDetail: fare - index.meanFare, detail: `Mean: $${index.meanFare.toFixed(2)}, StdDev: $${index.stdDevFare.toFixed(2)}` }
    ));
  } else if (fare < lowerBound && lowerBound > 0) {
    const deviation = ((index.meanFare - fare) / index.stdDevFare).toFixed(1);
    flags.push(makeFlag('outlier_pricing', 'warning',
      `Fare $${fare.toFixed(2)} is ${deviation} std devs below mean ($${index.meanFare.toFixed(2)})`,
      { numericDetail: index.meanFare - fare, detail: `Mean: $${index.meanFare.toFixed(2)}, StdDev: $${index.stdDevFare.toFixed(2)}` }
    ));
  }

  return flags;
}

// ─── Main Detection Orchestrator ────────────────────────────────────

/** All detector functions mapped by type */
const DETECTORS: Record<AnomalyType, (row: ExportJobRow, config: AnomalyConfig, index: RowIndex) => AnomalyFlag[]> = {
  fare_variance: detectFareVariance,
  missing_return: detectMissingReturn,
  missing_fields: detectMissingFields,
  validation_issue: detectValidationIssues,
  tm_mismatch: detectTMMismatch,
  duplicate_trip: detectDuplicateTrips,
  outlier_pricing: detectOutlierPricing,
};

/**
 * Run all enabled anomaly detectors against the filtered dataset.
 * Returns per-row results and a summary.
 *
 * This is the main entry point for the anomaly detection engine.
 * Structured so future AI layer can consume the results.
 */
export function runAnomalyDetection(
  rows: ExportJobRow[],
  config: AnomalyConfig
): {
  results: Map<string, RowAnomalyResult>;
  summary: AnomalySummary;
} {
  // Reset flag counter for this run
  flagCounter = 0;

  // Build indexes for efficient lookups
  const index = buildIndex(rows);

  // Run detectors for each row
  const results = new Map<string, RowAnomalyResult>();

  const allTypes: AnomalyType[] = [
    'fare_variance', 'missing_return', 'missing_fields',
    'validation_issue', 'tm_mismatch', 'duplicate_trip', 'outlier_pricing',
  ];

  for (const row of rows) {
    const allFlags: AnomalyFlag[] = [];

    for (const type of allTypes) {
      if (!config.enabledTypes.has(type)) continue;
      const detector = DETECTORS[type];
      if (detector) {
        const flags = detector(row, config, index);
        allFlags.push(...flags);
      }
    }

    if (allFlags.length > 0) {
      results.set(row.tc_id, {
        rowId: row.tc_id,
        jobId: row.job_id,
        flags: allFlags,
        reviewStatus: 'flagged',
        reviewNotes: null,
        reviewedBy: null,
        reviewedAt: null,
      });
    }
  }

  // Compute summary
  const byType: Record<AnomalyType, number> = {} as any;
  for (const type of allTypes) byType[type] = 0;

  let totalFlags = 0;
  for (const result of results.values()) {
    for (const flag of result.flags) {
      byType[flag.type] = (byType[flag.type] || 0) + 1;
      totalFlags++;
    }
  }

  const summary: AnomalySummary = {
    totalFlagged: results.size,
    totalFlags,
    byType,
    reviewed: 0,
    ignored: 0,
    unreviewed: results.size,
  };

  return { results, summary };
}

/**
 * Merge persisted review statuses into anomaly results.
 * Called after loading reviews from the database.
 */
export function mergeReviewStatuses(
  results: Map<string, RowAnomalyResult>,
  reviews: { job_id: string; anomaly_type: string; status: string; notes: string | null; reviewed_by: string | null; reviewed_at: string | null }[]
): { results: Map<string, RowAnomalyResult>; summary: AnomalySummary } {
  // Build a lookup: job_id → best review status
  const reviewMap = new Map<string, { status: string; notes: string | null; reviewed_by: string | null; reviewed_at: string | null }>();
  for (const review of reviews) {
    // Use the most recent review per job
    const existing = reviewMap.get(review.job_id);
    if (!existing || (review.status === 'reviewed' || review.status === 'ignored')) {
      reviewMap.set(review.job_id, review);
    }
  }

  // Merge into results
  for (const [rowId, result] of results) {
    const review = reviewMap.get(result.jobId);
    if (review) {
      result.reviewStatus = review.status as any;
      result.reviewNotes = review.notes;
      result.reviewedBy = review.reviewed_by;
      result.reviewedAt = review.reviewed_at;
    }
  }

  // Recompute summary
  const allTypes: AnomalyType[] = [
    'fare_variance', 'missing_return', 'missing_fields',
    'validation_issue', 'tm_mismatch', 'duplicate_trip', 'outlier_pricing',
  ];
  const byType: Record<AnomalyType, number> = {} as any;
  for (const type of allTypes) byType[type] = 0;

  let totalFlags = 0;
  let reviewed = 0;
  let ignored = 0;
  let unreviewed = 0;

  for (const result of results.values()) {
    for (const flag of result.flags) {
      byType[flag.type] = (byType[flag.type] || 0) + 1;
      totalFlags++;
    }
    if (result.reviewStatus === 'reviewed') reviewed++;
    else if (result.reviewStatus === 'ignored') ignored++;
    else unreviewed++;
  }

  return {
    results,
    summary: { totalFlagged: results.size, totalFlags, byType, reviewed, ignored, unreviewed },
  };
}
